import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import type { AgentContext } from "src/components/llm/agent-focus/agent-focus";
import type { ComposerContextChip } from "src/components/llm/components/composer-context-chip";
import type { ComposerFile } from "src/components/llm/utils/file-processing";
import type { InteractionContextInput } from "src/graphql";
import { noop } from "ts-essentials";

import type { AndOrCondition, Condition } from "src/types/visual";

export type AgentNudgeMetadata = {
  context?: AgentContext;
  focusedConditions?: AndOrCondition<Condition>;
  focusedLabel?: string;
  autoSubmit?: boolean;
  /** Attachments sent with an auto-submitted prompt. */
  files?: ComposerFile[];
  /** Puts the cursor in the composer once the panel shows it, with or without a prompt. */
  focusComposer?: boolean;
  /**
   * Leaves a closed panel closed while the prompt is sent.
   * @default false
   */
  stayClosed?: boolean;
  /** Hears whether an auto-submitted prompt reached the chat, once the chat knows. */
  onSent?: (sent: boolean) => void;
  /** A preset request the composer shows as a chip, for the person to add to before they send. */
  promptChip?: AgentPromptChip;
};

/** A preset request shown as a composer chip, which the next message sends before the typed text. */
export type AgentPromptChip = Pick<ComposerContextChip, "label" | "icon"> & {
  prompt: string;
};

/** A `stayClosed` send that did not go: the closed panel can't say why. */
export const NUDGE_NOT_SENT_TOAST = {
  id: "agent-nudge-not-sent",
  title: "Couldn't send your message",
  message: "Open the chat to check it's ready, then send again.",
  variant: "error",
} as const;

/** Returns true when it took the prompt. */
type NudgeListener = (prompt: string, metadata?: AgentNudgeMetadata) => boolean;
type LoadSessionListener = (sessionId: string) => void;
type StartNewChatListener = () => void;
/** A composer outside the panel, whose picks the new chat sends in place of the panel's own. */
export type NewChatComposer = {
  context: AgentContext;
  interactionContext?: InteractionContextInput;
};
/** Resolves whether the chat was created. */
type StartChatListener = (
  prompt: string,
  files: ComposerFile[],
  composer?: NewChatComposer,
) => Promise<boolean>;
export type AgentOpenOptions = {
  /** Leaves a panel that would overlay the page closed. */
  onlyIfDocked?: boolean;
};

/** Returns how to put the panel back when it opened one the person had closed. */
type OpenListener = (options?: AgentOpenOptions) => (() => void) | void;

export type AgentControlContextValue = {
  agentAvailable: boolean;
  // Temporary resize guard: only embedded v1 chats publish this; not all agent hosts report activity.
  // Remove with v1; v2 supports steering and does not need this guard.
  isV1SessionActive: boolean;
  setIsV1SessionActive: (active: boolean) => void;
  /** Send a prompt to the agent pane (opens the panel if closed, unless `stayClosed`). */
  nudgeAgent?: NudgeListener;
  /** Open the agent panel and load a session into it. */
  loadSession?: LoadSessionListener;
  /** Open the agent panel on a fresh chat. */
  startNewChat?: StartNewChatListener;
  /** Whether a panel is listening for startNewChat; false until a lazily loaded panel mounts. */
  canStartNewChat: boolean;
  /**
   * Create a chat with this first message, then open the panel on it.
   * Undefined until a panel can create chats.
   */
  startChat?: StartChatListener;
  /**
   * Open the agent panel as it is, without saving that as the person's choice.
   * Returns a callback that puts it back to their saved open state. Undefined
   * until a lazily loaded panel listens.
   */
  openAgent?: (options?: AgentOpenOptions) => () => void;
  /** Registrations return a cleanup callback for useEffect. */
  registerNudgeListener: (listener: NudgeListener) => () => void;
  registerLoadSessionListener: (listener: LoadSessionListener) => () => void;
  registerStartNewChatListener: (listener: StartNewChatListener) => () => void;
  registerStartChatListener: (listener: StartChatListener) => () => void;
  registerOpenListener: (listener: OpenListener) => () => void;
};

const AgentControlContext = createContext<AgentControlContextValue>({
  agentAvailable: false,
  isV1SessionActive: false,
  setIsV1SessionActive: noop,
  canStartNewChat: false,
  registerNudgeListener: () => noop,
  registerLoadSessionListener: () => noop,
  registerStartNewChatListener: () => noop,
  registerStartChatListener: () => noop,
  registerOpenListener: () => noop,
});
AgentControlContext.displayName = "AgentControlContext";
export const useAgentControlContext = () => useContext(AgentControlContext);

/** Page controls and the agent panel share commands within this provider. */
export function AgentControlProvider({
  agentAvailable,
  children,
}: PropsWithChildren<{ agentAvailable: boolean }>) {
  const [isV1SessionActive, setIsV1SessionActive] = useState(false);
  const nudgeListeners = useRef(new Set<NudgeListener>());
  const loadSessionListeners = useRef(new Set<LoadSessionListener>());
  const startNewChatListeners = useRef(new Set<StartNewChatListener>());
  const openListeners = useRef(new Set<OpenListener>());
  const startChatListener = useRef<StartChatListener | null>(null);
  const [hasStartChatListener, setHasStartChatListener] = useState(false);
  const [startNewChatListenerCount, setStartNewChatListenerCount] = useState(0);
  const [hasOpenListener, setHasOpenListener] = useState(false);
  const fireOpenListeners = useCallback((options?: AgentOpenOptions) => {
    const restores = [...openListeners.current].map((listener) =>
      listener(options),
    );
    return () => {
      for (const restore of restores) restore?.();
    };
  }, []);
  const nudgeAgent = useCallback(
    (prompt: string, metadata?: AgentNudgeMetadata) => {
      if (!metadata?.stayClosed) fireOpenListeners();
      let isTaken = false;
      for (const listener of nudgeListeners.current)
        if (listener(prompt, metadata)) isTaken = true;
      return isTaken;
    },
    [fireOpenListeners],
  );
  const loadSession = useCallback(
    (sessionId: string) => {
      fireOpenListeners();
      for (const listener of loadSessionListeners.current) listener(sessionId);
    },
    [fireOpenListeners],
  );
  const startNewChat = useCallback(() => {
    fireOpenListeners();
    for (const listener of startNewChatListeners.current) listener();
  }, [fireOpenListeners]);
  // The panel opens only once the chat exists, so it never shows the step before it.
  const startChat = useCallback(
    async (
      prompt: string,
      files: ComposerFile[],
      composer?: NewChatComposer,
    ) => {
      const created =
        (await startChatListener.current?.(prompt, files, composer)) ?? false;
      if (created) fireOpenListeners();
      return created;
    },
    [fireOpenListeners],
  );
  const registerStartChatListener = useCallback(
    (listener: StartChatListener) => {
      startChatListener.current = listener;
      setHasStartChatListener(true);
      return () => {
        if (startChatListener.current !== listener) return;
        startChatListener.current = null;
        setHasStartChatListener(false);
      };
    },
    [],
  );
  const registerNudgeListener = useCallback((listener: NudgeListener) => {
    nudgeListeners.current.add(listener);
    return () => {
      nudgeListeners.current.delete(listener);
    };
  }, []);
  const registerLoadSessionListener = useCallback(
    (listener: LoadSessionListener) => {
      loadSessionListeners.current.add(listener);
      return () => {
        loadSessionListeners.current.delete(listener);
      };
    },
    [],
  );
  const registerStartNewChatListener = useCallback(
    (listener: StartNewChatListener) => {
      startNewChatListeners.current.add(listener);
      setStartNewChatListenerCount(startNewChatListeners.current.size);
      return () => {
        startNewChatListeners.current.delete(listener);
        setStartNewChatListenerCount(startNewChatListeners.current.size);
      };
    },
    [],
  );
  const registerOpenListener = useCallback((listener: OpenListener) => {
    openListeners.current.add(listener);
    setHasOpenListener(true);
    return () => {
      openListeners.current.delete(listener);
      setHasOpenListener(openListeners.current.size > 0);
    };
  }, []);
  const value = useMemo(
    () => ({
      agentAvailable,
      isV1SessionActive,
      setIsV1SessionActive,
      nudgeAgent,
      loadSession,
      startNewChat,
      canStartNewChat: startNewChatListenerCount > 0,
      startChat: hasStartChatListener ? startChat : undefined,
      openAgent: hasOpenListener ? fireOpenListeners : undefined,
      registerNudgeListener,
      registerLoadSessionListener,
      registerStartNewChatListener,
      registerStartChatListener,
      registerOpenListener,
    }),
    [
      agentAvailable,
      isV1SessionActive,
      nudgeAgent,
      loadSession,
      startNewChat,
      startNewChatListenerCount,
      hasStartChatListener,
      startChat,
      hasOpenListener,
      fireOpenListeners,
      registerNudgeListener,
      registerLoadSessionListener,
      registerStartNewChatListener,
      registerStartChatListener,
      registerOpenListener,
    ],
  );
  return (
    <AgentControlContext.Provider value={value}>
      {children}
    </AgentControlContext.Provider>
  );
}
